import React, { useState, useEffect, useRef } from 'react';
import '../CSS/Header.css';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons/faSignOutAlt';
import { faUser } from '@fortawesome/free-regular-svg-icons/faUser';
import useAuth from '../Hooks/useAuth';

const Header = React.memo(() => {
    const { isAuthenticated, refreshAuthStatus, isAdmin, user, role } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const userDetailsRef = useRef(null);
    const [showUserDetails, setShowUserDetails] = useState(false);
    const [navOpen, setNavOpen] = useState(false);

    // Close the mobile menu on every navigation (auth status is refreshed
    // by the actions that change it — login/signup/logout — not on a timer
    // or on every route change).
    useEffect(() => {
        setNavOpen(false);
    }, [location]);

    const handleLogout = () => {
        // Logout user
        fetch(`${process.env.REACT_APP_hostURL}/auth/logout`, {
            method: 'POST',
            credentials: 'include',
        })
            .then((response) => {
                if (response.ok) {
                    navigate('/', { replace: true });
                    refreshAuthStatus();
                } else {
                    console.error('Error:', response);
                }
            })
            .catch((error) => {
                console.error('Error:', error);
            });
    };
    const handleMouseEnter = () => {
        setShowUserDetails(true);
    };

    const handleMouseLeave = () => {
        setShowUserDetails(false);
    };

    const handleClickOutside = (event) => {
        if (userDetailsRef.current && !userDetailsRef.current.contains(event.target)) {
            setShowUserDetails(true);
        }
    };
    useEffect(() => {
        if (showUserDetails) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserDetails]);

    return (
        <>
            <div id='info-bar'>
                <div id='child-info-bar'>
                    <span><FontAwesomeIcon className="mx-2" icon={faEnvelope} />support.itme@krmangalam.edu.in</span>
                    <span>K.R. Mangalam University</span>
                </div>
            </div>
            <div id="topBar">
                {/* for wider screen */}
                <div id="innerBar-max" className='d-none d-md-flex'>
                    <div className="header-left-logo">
                        <picture>
                            <source srcSet="/itme-2-260.webp" type="image/webp" />
                            <img src="/itme-2-260.png" alt="ITME Journal Logo" className="logo-max" width="260" height="260" fetchpriority="high" decoding="async" />
                        </picture>
                    </div>
                    <div id='childBar-max'>
                        <h2 className="jnhd-max">INNOVATIVE TRENDS IN MULTIDISCIPLINARY ENGINEERING</h2>
                        <div className="header-subtitle">
                            <span className="issn-text">International Peer-Reviewed Journal of the School of Engineering &amp; Technology , K.R. Mangalam University, Gurugram.</span>
                        </div>
                    </div>
                    <div className="header-right-logo">
                        <picture>
                            <source srcSet="/krmu-260.webp" type="image/webp" />
                            <img src="/krmu-260.png" alt="KRMU Logo" className="itme-logo-max" width="260" height="260" fetchpriority="high" decoding="async" />
                        </picture>
                    </div>
                </div>
                {/* for tablet and mobile */}
                <div id="innerBar-min" className='d-md-none'>
                    <picture>
                        <source srcSet="/itme-2-260.webp" type="image/webp" />
                        <img className="logo-min" src="/itme-2-260.png" alt="ITME Journal Logo" width="260" height="260" fetchpriority="high" decoding="async" />
                    </picture>
                    <div id='childBar-min'>
                        <h2 className="jnhd-min">INNOVATIVE TRENDS IN MULTIDISCIPLINARY ENGINEERING</h2>
                        <div className="header-subtitle-mobile">
                            <span className="issn-text-mobile">International Journal</span>
                        </div>
                    </div>
                    <picture>
                        <source srcSet="/krmu-260.webp" type="image/webp" />
                        <img className="logo-min" src="/krmu-260.png" alt="KRMU Logo" width="260" height="260" decoding="async" />
                    </picture>
                </div>
            </div>

            <nav className="navbar navbar-expand-lg navbar-light mx-auto navbar-custom">
                <button
                    className="navbar-toggler"
                    type="button"
                    onClick={() => setNavOpen((open) => !open)}
                    aria-controls="navbarNav"
                    aria-expanded={navOpen}
                    aria-label="Toggle navigation"
                >
                    <span className="navbar-toggler-icon"></span>
                </button>

                {/* Buttons that stay outside the collapsible part */}
                <div className="d-flex ms-auto d-lg-none">
                    {isAuthenticated ? (
                        <>
                            <button
                                className="btn btn-outline-success mx-2"
                                id="User_btn"
                                onClick={() => setShowUserDetails(!showUserDetails)}
                                onMouseEnter={handleMouseEnter}
                            >
                                <FontAwesomeIcon icon={faUser} />
                            </button>
                            <button className="btn btn-outline-success mx-2" id="Logout_btn" onClick={handleLogout}>
                                <FontAwesomeIcon icon={faSignOutAlt} aria-hidden="true" />
                            </button>
                            {showUserDetails && (
                                <div
                                    ref={userDetailsRef}
                                    className="user-details-dropdown"
                                    style={{
                                        position: 'absolute',
                                        top: '60px',
                                        right: '20px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #ccc',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                    }}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <p><strong>User Dashboard</strong></p>
                                    <p>Name: {user.name}</p>
                                    <p>Email: {user.email}</p>
                                    {role === 'reviewer' ? (
                                        <Link to="/reviewer/dashboard">Go to Dashboard</Link>
                                    ) : (
                                        <Link to="/dashboard">Go to Dashboard</Link>
                                    )}
                                    <Link to="/profile" style={{ display: 'block', marginTop: '5px' }}>View Profile</Link>
                                </div>
                            )}
                        </>
                    ) : (
                        // Login / Signup hidden during initial launch
                        null
                    )}
                </div>

                <div className={`collapse navbar-collapse${navOpen ? ' show' : ''}`} id="navbarNav">
                    <ul className="navbar-nav me-auto nav-link-custom">
                        <li className="nav-item active">
                            <Link className="nav-link" to="/">
                                ITME Home <span className="visually-hidden">(current)</span>
                            </Link>
                        </li>
                        <li className="nav-item dropdown custom-hover-dropdown">
                            <Link className="nav-link dropdown-toggle" to="/about" role="button" aria-expanded="false" style={{ display:'inline-flex', alignItems:'center', gap:4 }}>About</Link>
                            <ul className="dropdown-menu about-menu" role="menu" aria-label="About navigation">
                                <li role="none"><Link role="menuitem" className="dropdown-item" to="/about">About ITME</Link></li>
                                <li role="none"><Link role="menuitem" className="dropdown-item" to="/krmangalam-university">K.R. Mangalam University</Link></li>
                            </ul>
                        </li>
                        <li className="nav-item">
                            <Link className="nav-link" to="/abstracting-indexing">Abstracting &amp; Indexing</Link>
                        </li>

                        {/* Dropdown for Publications */}
                        <li className="nav-item dropdown custom-hover-dropdown">
                            <Link className="nav-link dropdown-toggle" to="/current-issue" role="button" aria-expanded="false">Publications</Link>
                            <ul className="dropdown-menu">
                                <li><Link className="dropdown-item" to="/current-issue">Current Issue</Link></li>
                                <li><Link className="dropdown-item" to="/archives">Archives</Link></li>
                            </ul>
                        </li>

                        <li className="nav-item">
                            <Link className="nav-link" to="/editorial-leadership">Editorial Leadership</Link>
                        </li>

                        <li className="nav-item dropdown custom-hover-dropdown">
                            <Link className="nav-link dropdown-toggle" to="#" role="button" aria-expanded="false">Policy &amp; Ethics</Link>
                            <ul className="dropdown-menu">
                                <li><Link className="dropdown-item" to="/ethics">Ethics</Link></li>
                                <li><Link className="dropdown-item" to="/itme-policies-framework">Policies</Link></li>

                                <li><Link className="dropdown-item" to="/terms-and-conditions">Terms &amp; Conditions</Link></li>
                            </ul>
                        </li>

                        {/* Submit Manuscripts dropdown */}
                        <li className="nav-item dropdown custom-hover-dropdown">
                            <Link className="nav-link dropdown-toggle" to="/form" role="button" aria-expanded="false">Submit Manuscripts</Link>
                            <ul className="dropdown-menu">
                                <li><Link className="dropdown-item" to="/form">Submit Manuscripts</Link></li>
                                <li><Link className="dropdown-item" to="/author-guidelines">Author Guidelines</Link></li>
                            </ul>
                        </li>

                        {/* Help link redirecting to Contact Us */}
                        <li className="nav-item">
                            <Link className="nav-link" to="/contact">Help</Link>
                        </li>

                        {isAdmin && (
                            <li className="nav-item">
                                <Link className="nav-link" to="/admin">Admin</Link>
                            </li>
                        )}
                    </ul>
                    {/* Buttons on the right for larger screens */}
                    <div className="d-none d-lg-flex align-items-center">
                        {isAuthenticated ? (
                            <>
                                <button
                                    className="btn btn-outline-success mx-2"
                                    id="User_btn"
                                    onClick={() => setShowUserDetails(!showUserDetails)}
                                    onMouseEnter={handleMouseEnter}
                                >
                                    <FontAwesomeIcon icon={faUser} />
                                </button>
                                <button className="btn btn-outline-success mx-2" id="Logout_btn" onClick={handleLogout}>
                                    <FontAwesomeIcon icon={faSignOutAlt} aria-hidden="true" />
                                </button>
                                {showUserDetails && (
                                    <div
                                        ref={userDetailsRef}
                                        className="user-details-dropdown"
                                        style={{
                                            position: 'absolute',
                                            top: '60px',
                                            right: '20px',
                                            backgroundColor: '#fff',
                                            border: '1px solid #ccc',
                                            padding: '10px',
                                            borderRadius: '5px',
                                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
                                        }}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <p><strong>User Dashboard</strong></p>
                                        <p>Name: {user.name}</p>
                                        <p>Email: {user.email}</p>
                                        {role === 'reviewer' ? (
                                            <Link to="/reviewer/dashboard">Go to Dashboard</Link>
                                        ) : (
                                            <Link to="/dashboard">Go to Dashboard</Link>
                                        )}
                                        <Link to="/profile" style={{ display: 'block', marginTop: '5px' }}>View Profile</Link>
                                    </div>
                                )}
                            </>
                        ) : null}
                    </div>
                </div>
            </nav>
        </>
    );
});

export default Header;
